import { type ReactElement } from 'react';
import { useEffect } from 'react';
import { Box, Button, Stack } from '@mui/material';
import {
  ProfileStepIds,
  type RecommendationProfile,
} from '@/models/recommendation';
import { useProfileQuiz } from '@/packages/profile-quiz/context/profile-quiz';
import { ProfileQuizProvider } from '@/packages/profile-quiz/context/profile-quiz/provider';
import type { QuestionProps } from '@/packages/profile-quiz/context/profile-quiz/types';
import { useScrollToEnd } from '@/packages/profile-quiz/context/scroll-to-end';
import { ScrollToEndProvider } from '@/packages/profile-quiz/context/scroll-to-end/provider';

interface QuizProps {
  profile: RecommendationProfile;
  children: ReactElement<QuestionProps>[];
}

const ScrollToEndOnStepChange = () => {
  const { visibleQuestionIds, canGoBack } = useProfileQuiz();
  const scrollToEndContext = useScrollToEnd();
  const isFirstStep = visibleQuestionIds.length === 1 && !canGoBack;

  useEffect(() => {
    if (!isFirstStep) scrollToEndContext?.scrollToEnd();
  }, [
    visibleQuestionIds,
    isFirstStep,
    scrollToEndContext?.scrollToEnd,
    scrollToEndContext,
  ]);

  return null;
};

const QuizRenderer = ({
  questionElements,
}: {
  questionElements: ReactElement<QuestionProps>[];
}) => {
  const {
    visibleQuestionIds,
    submit,
    goBack,
    canGoBack,
    canGoNext,
    isLastQuestion,
  } = useProfileQuiz();
  const { endRef } = useScrollToEnd();

  const visibleQuestions = questionElements.filter((child) =>
    visibleQuestionIds.includes(child.props.id)
  );

  const isFirstStep = visibleQuestionIds.length === 1 && !canGoBack;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      <Stack minHeight="100%" justifyContent="space-between" gap={3}>
        <Stack gap={5}>
          {visibleQuestions.map((question) => (
            <Box key={question.props.id}>{question}</Box>
          ))}
        </Stack>

        {!isFirstStep && (
          <Stack pt={2} gap={2} direction="row">
            <Button variant="text" onClick={goBack} disabled={!canGoBack}>
              Voltar
            </Button>

            <Button variant="contained" onClick={submit} disabled={!canGoNext}>
              {isLastQuestion ? 'Começar minha jornada' : 'Próximo'}
            </Button>
          </Stack>
        )}
      </Stack>
      <Box ref={endRef} />
    </Box>
  );
};

const PresentationFinishedStep: QuestionProps = {
  id: ProfileStepIds.PresentationFinished,
};

export const Quiz = ({ profile, children }: QuizProps) => {
  const questionsConfig: QuestionProps[] = [
    ...children.map((child) => child.props),
    PresentationFinishedStep,
  ];

  return (
    <ProfileQuizProvider questions={questionsConfig} profile={profile}>
      <ScrollToEndProvider>
        <ScrollToEndOnStepChange />
        <QuizRenderer questionElements={children} />
      </ScrollToEndProvider>
    </ProfileQuizProvider>
  );
};
